import { z } from 'zod';
import { Ulid } from './common.js';
import { UPDATE_ASK_MAX_ACCOUNTS, UPDATE_ASK_SERVER_MS, UpdateCode } from './update-ask.js';
/**
 * Whose Board a report came from (`docs/feedback-plan.md` §6.3): a feedback sheet on a Board asks
 * whether the BoardFlight that paired this Board is here, the way an update card asks
 * (`update-ask.ts`), so that a report typed on the Board can carry that account's name.
 *
 * Four parties read this module:
 *
 *  - the injected layers, web and Android, which log the code, ask, and send the proof with a report
 *    (their constants are baked in by `patcher/dump-constants.mjs`);
 *  - the Worker, which rings the candidate desktop apps, collects their answers until the deadline
 *    and names a report from the result (`apps/api/src/routes/feedback-ask.ts`);
 *  - the desktop app, which reads a ring's `purpose` and looks for this line rather than the update
 *    card's (`apps/desktop/core/src/rings.rs`);
 *  - the library's Account page, where an account switches this off and sees every report that
 *    names it.
 *
 * **The answer is blind** (§6.3 decision 5). The page is the developer's code, so every ask is
 * answered alike, at the deadline, with one seal of one size — whether an account answered, none
 * did, or nobody was rung. Only the Worker can open a seal, and a team learns a name only from a
 * report filed under it.
 */
/** The line the layer writes while a feedback ask rings, then the code. ASCII, like the update line. */
export const FEEDBACK_CHECK_LINE = '[BoardFlight] feedback-check ';
/**
 * The Android log tag the APK layer writes that line under (`Log.i`), and the only tag the desktop
 * app listens to in an APK's log. A web game's line has no tag of its own: Board Browser files a
 * page's console under `BoardWebApp:<appId>`. Whether a Board narrows an APK's listen to this tag,
 * and keeps another app's lines out of it, was not measured before APKs were turned on
 * (`docs/feedback-plan.md` §6.3, "Which games").
 */
export const FEEDBACK_LOG_TAG = 'BoardFlight';
/**
 * How long the Worker holds a feedback ask. It always answers at this moment, never sooner, so the
 * answer's timing says nothing. The layer stops writing its line at the same deadline.
 */
export const FEEDBACK_ASK_SERVER_MS = UPDATE_ASK_SERVER_MS;
/** How long the layer waits for the seal before giving up on it: the update card's thirty seconds. */
export const FEEDBACK_ASK_SECONDS = 30;
/** How long a seal names a report with. A sheet can stay open while somebody types on a Board. */
export const FEEDBACK_SEAL_MS = 30 * 60 * 1000;
/** More candidate accounts than this are not rung, as for an update ask. */
export const FEEDBACK_ASK_MAX_ACCOUNTS = UPDATE_ASK_MAX_ACCOUNTS;
/** At most this many reports a day name one account to one game from its Boards (§6.3). */
export const BOARD_NAMED_REPORTS_PER_DAY = 20;
/** A sealed answer. Opaque to the page; only the Worker can open one. */
export const FeedbackSeal = z.string().min(16).max(2048);
/** `POST /v1/feedback-layer/feedback-ask` — the same identification the update ask sends. */
export const FeedbackAskRequest = z.object({
    key: z.string().min(1).max(128),
    build: z.string().max(64),
    code: UpdateCode,
});
/** Always this shape, always at the deadline. */
export const FeedbackAskResponse = z.object({ seal: FeedbackSeal });
/**
 * What a report sent from a Board carries to be named, beside the rest of `POST /v1/feedback`'s
 * body: the seal once the ask has answered, the code before. Sent only while *Send anonymously* is
 * off. The Worker reads it apart from the report and leniently — a proof it cannot use leaves the
 * report anonymous, and never refuses it.
 */
export const FeedbackNameProof = z.union([
    z.object({ seal: FeedbackSeal }).strict(),
    z.object({ code: UpdateCode }).strict(),
]);
/**
 * How a report came to carry a name: `session`, a signed-in phone (§6.1); `board`, the account that
 * paired the Board it was typed on (§6.3). Null on the wire whenever the name is.
 */
export const ReportNamedBy = z.enum(['session', 'board']);
/**
 * `GET /v1/me/named-feedback` — one report that carries this account's name, either way it came by
 * it, with the words. A person named on words they did not type can see them and take their name
 * off (`DELETE /v1/me/named-feedback/:id`); the report stays with the team. No screenshot: that is
 * the team's (§7.3).
 */
export const NamedFeedbackReport = z.object({
    id: Ulid,
    game: z.object({ id: Ulid, name: z.string() }),
    kind: z.enum(['bug', 'feature']),
    message: z.string(),
    namedBy: ReportNamedBy,
    createdAt: z.number().int(),
});
export const NamedFeedbackPage = z.object({
    items: z.array(NamedFeedbackReport),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=feedback-ask.js.map